// Таблицата на справката „Инвентаризации": ниски редове на един ред. Широк
// екран — № · Дата · Склад · Въвел · Причина · Излишък · Липси · Нето · Бруто ·
// Статус; лаптоп/таблет — № · Дата · Склад (въвелият и причината приглушено
// до него) · Разлика „+3 / −2" · Бруто; телефон — карти. Филтрите са в
// заглавията (на средна ширина „Склад" носи и Въвел/Причина/Статус, а
// „Разлика" — Излишък/Липси); заглавията сортират. Лентите „Кухня · 2
// инвентаризации" идват готови от модела (групирането е по избор).
// Анулираните са зачертани, редът „Общо" е без тях. „Печат" и менюто „⋯" са
// на реда. Клавиатура: ↑/↓ по редовете, Enter отваря документа.
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { StocktakeDocument } from "../../../../api/inventoryStocktakes";
import { fmtDate, fmtDateTime } from "../../components/dateTime";
import { StatusPill } from "../../components/list/ListBits";
import { RowActionsMenu, type RowAction } from "../../components/list/RowActionsMenu";
import { sectionList, type ColumnSections } from "../../components/filters/useColumnFilters";
import {
  formatSignedMoney,
  isStocktakeAnnulled,
  signedToneClass,
} from "../../stocktake/components/stocktakeMeta";
import { ReportHead } from "../components/ReportListBits";
import {
  REPORT_TOTAL_LABEL_CLASS,
  REPORT_HEADER_ROW_CLASS,
  REPORT_OPENABLE_ROW_CLASS,
  REPORT_TOTAL_ROW_CLASS,
  moveRowFocus,
  openableRowProps,
} from "../components/reportListModel";
import type {
  StocktakeFilterKey,
  StocktakeListRow,
  StocktakeSort,
  StocktakeSortKey,
} from "./stocktakesReportModel";

export type StocktakesDensity = "full" | "medium" | "cards";

// Литерални класове — Tailwind сканира само пълни низове
const GRID_CLASS = {
  full: "grid grid-cols-[52px_128px_minmax(100px,.9fr)_minmax(90px,.8fr)_minmax(110px,1fr)_76px_72px_96px_100px_92px_100px] items-center gap-2.5 px-[18px]",
  medium: "grid grid-cols-[48px_92px_minmax(170px,1fr)_92px_100px_100px] items-center gap-2.5 px-[18px]",
} as const;

type Props = {
  rows: StocktakeListRow[];
  density: StocktakesDensity;
  sort: StocktakeSort;
  onSort: (key: StocktakeSortKey) => void;
  filters: ColumnSections<StocktakeFilterKey>;
  totalNet: number;
  totalGross: number;
  onOpen: (stocktake: StocktakeDocument) => void;
  onPrint: (stocktake: StocktakeDocument) => void;
  actionsOf: (stocktake: StocktakeDocument) => RowAction[];
  footer?: ReactNode;
};

export const StocktakesReportTable = ({
  rows,
  density,
  sort,
  onSort,
  filters,
  totalNet,
  totalGross,
  onOpen,
  onPrint,
  actionsOf,
  footer,
}: Props) => {
  const { t } = useTranslation();

  const annulledClass = (stocktake: StocktakeDocument) =>
    isStocktakeAnnulled(stocktake) ? "!text-faint line-through" : "";
  const statusPill = (stocktake: StocktakeDocument) =>
    isStocktakeAnnulled(stocktake) ? (
      <StatusPill tone="neutral">{t("inventoryPage.stocktakeStatusAnnulled")}</StatusPill>
    ) : (
      <StatusPill tone="ok">{t("inventoryPage.stocktakeStatusActive")}</StatusPill>
    );
  // „+3 / −2" — броят продукти с излишък и с липса
  const counts = (stocktake: StocktakeDocument) => (
    <span className={`font-sheet ${annulledClass(stocktake)}`}>
      <span className={stocktake.surplusCount ? "text-ok-deep" : "text-ghost"}>
        {stocktake.surplusCount ? `+${stocktake.surplusCount}` : "0"}
      </span>
      <span className="text-faint"> / </span>
      <span className={stocktake.shortageCount ? "text-danger-ink" : "text-ghost"}>
        {stocktake.shortageCount ? `-${stocktake.shortageCount}` : "0"}
      </span>
    </span>
  );
  const money = (stocktake: StocktakeDocument, value: number, strong = false) => (
    <span
      className={`text-right font-sheet ${strong ? "font-semibold" : ""} ${signedToneClass(value)} ${annulledClass(stocktake)}`}
    >
      {formatSignedMoney(value)}
    </span>
  );
  const groupBand = (name: string, count: number) => (
    <div
      key={`g-${name}`}
      className="border-t border-shade-2 bg-fill px-[18px] py-1.5 text-[12.5px] font-semibold leading-[normal] text-ink-2"
    >
      {name} · {t("inventoryPage.groupStocktakesCount", { count })}
    </div>
  );

  if (density === "cards") {
    return (
      <div onKeyDown={moveRowFocus}>
        {rows.map((row) => {
          if (row.kind === "group") return groupBand(row.name, row.count);
          const stocktake = row.stocktake;
          return (
            <div
              key={stocktake.id}
              {...openableRowProps(() => onOpen(stocktake))}
              className="flex cursor-pointer flex-col gap-1.5 border-t border-shade-1 px-3.5 py-3 outline-none focus-visible:bg-fill"
            >
              <div className="flex items-center gap-2">
                <span className="font-sheet font-semibold text-ink">№{stocktake.id}</span>
                <span className={`min-w-0 flex-1 truncate text-ink-2 ${annulledClass(stocktake)}`}>
                  {stocktake.warehouseName || "—"}
                </span>
                {statusPill(stocktake)}
                <RowActionsMenu actions={actionsOf(stocktake)} compact />
              </div>
              <div className="flex items-baseline justify-between gap-2.5 text-[12.5px] text-body">
                <span className="min-w-0 truncate">
                  {fmtDateTime(stocktake.date)} · {stocktake.createdByName || "—"}
                </span>
                <span className="flex shrink-0 items-baseline gap-2.5 text-[13px]">
                  {counts(stocktake)}
                  {money(stocktake, stocktake.totalValueWithVat || 0, true)}
                </span>
              </div>
            </div>
          );
        })}
        {footer}
      </div>
    );
  }

  const grid = GRID_CLASS[density];
  const full = density === "full";
  const head = (
    label: string,
    options: { sortKey?: StocktakeSortKey; align?: "right"; filters?: ReturnType<typeof sectionList> } = {},
  ) => <ReportHead label={label} sort={sort} onSort={onSort} {...options} />;

  return (
    <div className="overflow-x-auto">
      <div className={full ? "min-w-[1150px]" : "min-w-[690px]"} onKeyDown={moveRowFocus}>
        <div className={`${grid} ${REPORT_HEADER_ROW_CLASS}`}>
          {head("№")}
          {head(t("inventoryPage.colDate"), { sortKey: "date" })}
          {full ? (
            <>
              {head(t("inventoryPage.colWarehouse"), { sortKey: "warehouse", filters: sectionList(filters.warehouses) })}
              {head(t("inventoryPage.filterEnteredBy"), { sortKey: "user", filters: sectionList(filters.users) })}
              {head(t("inventoryPage.colReason"), { sortKey: "reason", filters: sectionList(filters.reasons) })}
              {head(t("inventoryPage.colSurplus"), {
                sortKey: "surplus",
                align: "right",
                filters: sectionList(filters.surplus),
              })}
              {head(t("inventoryPage.colShortage"), {
                sortKey: "shortage",
                align: "right",
                filters: sectionList(filters.shortage),
              })}
              {head(t("inventoryPage.colNet"), { sortKey: "net", align: "right" })}
            </>
          ) : (
            <>
              {head(t("inventoryPage.colWarehouse"), {
                sortKey: "warehouse",
                filters: sectionList(filters.warehouses, filters.users, filters.reasons, filters.statuses),
              })}
              {head(t("inventoryPage.colDiff"), { filters: sectionList(filters.surplus, filters.shortage) })}
            </>
          )}
          {head(t("inventoryPage.colGross"), { sortKey: "gross", align: "right" })}
          {full && head(t("inventoryPage.colStatus"), { filters: sectionList(filters.statuses) })}
          <span />
        </div>
        {rows.map((row) => {
          if (row.kind === "group") return groupBand(row.name, row.count);
          const stocktake = row.stocktake;
          return (
            <div
              key={stocktake.id}
              {...openableRowProps(() => onOpen(stocktake))}
              className={`${grid} ${REPORT_OPENABLE_ROW_CLASS}`}
            >
              <span className="font-sheet text-body">{stocktake.id}</span>
              <span className="whitespace-nowrap font-sheet text-ink-2">
                {full ? fmtDateTime(stocktake.date) : fmtDate(stocktake.date)}
              </span>
              {full ? (
                <>
                  <span className={`truncate text-ink ${annulledClass(stocktake)}`}>
                    {stocktake.warehouseName || "—"}
                  </span>
                  <span className="truncate font-medium text-ink">{stocktake.createdByName || "—"}</span>
                  <span className="truncate text-ink-2" title={stocktake.reason ?? undefined}>
                    {stocktake.reason || "—"}
                  </span>
                  <span
                    className={`text-right font-sheet font-bold ${
                      stocktake.surplusCount ? "text-ok-deep" : "text-ghost"
                    } ${annulledClass(stocktake)}`}
                  >
                    {stocktake.surplusCount ? `+${stocktake.surplusCount}` : "—"}
                  </span>
                  <span
                    className={`text-right font-sheet font-bold ${
                      stocktake.shortageCount ? "text-danger-ink" : "text-ghost"
                    } ${annulledClass(stocktake)}`}
                  >
                    {stocktake.shortageCount ? `-${stocktake.shortageCount}` : "—"}
                  </span>
                  {money(stocktake, stocktake.totalValueNoVat || 0)}
                </>
              ) : (
                <>
                  <span className="flex min-w-0 items-center gap-2">
                    <span className={`shrink-0 font-medium text-ink ${annulledClass(stocktake)}`}>
                      {stocktake.warehouseName || "—"}
                    </span>
                    {isStocktakeAnnulled(stocktake) && statusPill(stocktake)}
                    <span className="min-w-0 truncate text-muted" title={stocktake.reason ?? undefined}>
                      · {[stocktake.createdByName || "—", stocktake.reason].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  {counts(stocktake)}
                </>
              )}
              {money(stocktake, stocktake.totalValueWithVat || 0, true)}
              {full && <span className="flex min-w-0">{statusPill(stocktake)}</span>}
              <RowActionsMenu actions={actionsOf(stocktake)} onPrint={() => onPrint(stocktake)} dense />
            </div>
          );
        })}
        {footer}
        {rows.length > 0 && (
          <div className={`${grid} ${REPORT_TOTAL_ROW_CLASS}`}>
            <span className={`${REPORT_TOTAL_LABEL_CLASS} ${full ? "col-span-7" : "col-span-4"}`}>
              {t("inventoryPage.periodTotalActive")}
            </span>
            {full && (
              <span className={`text-right font-sheet font-semibold ${signedToneClass(totalNet)}`}>
                {formatSignedMoney(totalNet)}
              </span>
            )}
            <span className="text-right font-sheet font-bold text-brand-deep-ink">
              {formatSignedMoney(totalGross)}
            </span>
            {full && <span />}
            <span />
          </div>
        )}
      </div>
    </div>
  );
};

export default StocktakesReportTable;
